import React, { FC, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { EducationRowsForm } from "../EducationRowsForm";
import { ProfessionalRowsForm } from "../ProfessionalRowsForm";
import { GradingRowsForm } from "../GradingRowsForm";
import type {
  AnalysisParameters,
  EducationTrainingItem,
  ProfessionalPositionItem,
  GradingItem,
} from "../../../../hooks";

import { Button } from "@components/base";

type AccordionPanelId =
  | "education"
  | "professional"
  | "grading1"
  | "grading2"
  | "grading3"
  | false;

interface ReferentialsSectionProps {
  parameters: AnalysisParameters;
  onEducationAndTrainingChange: (
    index: number,
    field: keyof EducationTrainingItem,
    value: string | number,
  ) => void;
  onProfessionalPositionChange: (
    index: number,
    field: keyof ProfessionalPositionItem,
    value: string | number,
  ) => void;
  onAddEducationAndTraining: () => void;
  onRemoveEducationAndTraining: (index: number) => void;
  onAddProfessionalPosition: () => void;
  onRemoveProfessionalPosition: (index: number) => void;
  onGrading1Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading2Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading3Change: (index: number, field: keyof GradingItem, value: string) => void;
  onAddGrading1: () => void;
  onAddGrading2: () => void;
  onAddGrading3: () => void;
  onRemoveGrading1: (index: number) => void;
  onRemoveGrading2: (index: number) => void;
  onRemoveGrading3: (index: number) => void;
  onSaveReferentials: () => void;
  hasIdConflicts: boolean;
  showSaveSuccess: boolean;
}

export const ReferentialsSection: FC<ReferentialsSectionProps> = ({
  parameters,
  onEducationAndTrainingChange,
  onProfessionalPositionChange,
  onAddEducationAndTraining,
  onRemoveEducationAndTraining,
  onAddProfessionalPosition,
  onRemoveProfessionalPosition,
  onGrading1Change,
  onGrading2Change,
  onGrading3Change,
  onAddGrading1,
  onAddGrading2,
  onAddGrading3,
  onRemoveGrading1,
  onRemoveGrading2,
  onRemoveGrading3,
  onSaveReferentials,
  hasIdConflicts,
  showSaveSuccess,
}) => {
  const { t } = useTranslation();
  const [expandedPanel, setExpandedPanel] = useState<AccordionPanelId>("education");

  const handlePanelChange =
    (panel: AccordionPanelId) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const accordionSx = {
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  };

  const accordionSummarySx = {
    backgroundColor: "grey.50",
    borderRadius: 1,
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
    "& .MuiAccordionSummary-content": {
      margin: "12px 0",
      "&.Mui-expanded": {
        margin: "12px 0",
      },
    },
  };

  const accordionDetailsSx = {
    padding: 2,
    paddingTop: 2,
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Accordion
        expanded={expandedPanel === "education"}
        onChange={handlePanelChange("education")}
        sx={accordionSx}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon="chevron-down" size={20} />}
          sx={accordionSummarySx}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2">
              {t("paygap.referentials.education")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("paygap.referentials.educationLimit")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <EducationRowsForm
            educationAndTraining={parameters.educationAndTraining}
            parameters={parameters}
            onEducationAndTrainingChange={onEducationAndTrainingChange}
            onAddEducationAndTraining={onAddEducationAndTraining}
            onRemoveEducationAndTraining={onRemoveEducationAndTraining}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === "professional"}
        onChange={handlePanelChange("professional")}
        sx={accordionSx}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon="chevron-down" size={20} />}
          sx={accordionSummarySx}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2">
              {t("paygap.referentials.professional")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("paygap.referentials.professionalLimit")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <ProfessionalRowsForm
            professionalPositions={parameters.professionalPositions}
            parameters={parameters}
            onProfessionalPositionChange={onProfessionalPositionChange}
            onAddProfessionalPosition={onAddProfessionalPosition}
            onRemoveProfessionalPosition={onRemoveProfessionalPosition}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === "grading1"}
        onChange={handlePanelChange("grading1")}
        sx={accordionSx}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon="chevron-down" size={20} />}
          sx={accordionSummarySx}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2">
              {t("paygap.referentials.grading1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("paygap.referentials.gradingLimit")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <GradingRowsForm
            grading={parameters.grading1}
            gradingKey="grading1"
            parameters={parameters}
            maxRows={200}
            onGradingChange={onGrading1Change}
            onAddGrading={onAddGrading1}
            onRemoveGrading={onRemoveGrading1}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === "grading2"}
        onChange={handlePanelChange("grading2")}
        sx={accordionSx}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon="chevron-down" size={20} />}
          sx={accordionSummarySx}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2">
              {t("paygap.referentials.grading2")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("paygap.referentials.gradingLimit")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <GradingRowsForm
            grading={parameters.grading2}
            gradingKey="grading2"
            parameters={parameters}
            maxRows={200}
            onGradingChange={onGrading2Change}
            onAddGrading={onAddGrading2}
            onRemoveGrading={onRemoveGrading2}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expandedPanel === "grading3"}
        onChange={handlePanelChange("grading3")}
        sx={accordionSx}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon="chevron-down" size={20} />}
          sx={accordionSummarySx}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2">
              {t("paygap.referentials.grading3")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("paygap.referentials.gradingLimit")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <GradingRowsForm
            grading={parameters.grading3}
            gradingKey="grading3"
            parameters={parameters}
            maxRows={200}
            onGradingChange={onGrading3Change}
            onAddGrading={onAddGrading3}
            onRemoveGrading={onRemoveGrading3}
          />
        </AccordionDetails>
      </Accordion>
      {showSaveSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t("organizationSettings.settingsSaved")}
        </Alert>
      )}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        alignItems="center"
        mt={2}
      >
        {hasIdConflicts && (
          <Typography variant="body2" color="error">
            {t("paygap.referentials.uniqueId")}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={onSaveReferentials}
          disabled={hasIdConflicts}
        >
          {t("paygap.referentials.save")}
        </Button>
      </Box>
    </Box>
  );
};
